import type { SVGProps } from "react";

/*
  A small, hand-drawn icon set.

  Drawn here rather than pulled from a package so the whole application ships
  one consistent 24-grid stroke weight, and so the bundle carries only the
  glyphs actually used.
*/

type IconProps = SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  ...props,
});

export const Icon = {
  search: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </svg>
  ),
  bell: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
      <path d="M13.7 20a2 2 0 0 1-3.4 0" />
    </svg>
  ),
  sparkle: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m12 3 2 5.5L19.5 10 14 12l-2 5.5L10 12 4.5 10 10 8.5z" />
      <path d="M18.5 16.5 19 18l1.5.5L19 19l-.5 1.5L18 19l-1.5-.5L18 18z" />
    </svg>
  ),
  home: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z" />
    </svg>
  ),
  grid: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </svg>
  ),
  calendar: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
    </svg>
  ),
  users: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="9" cy="8.5" r="3.4" />
      <path d="M3.5 20c0-3.2 2.5-5.3 5.5-5.3s5.5 2.1 5.5 5.3" />
      <path d="M16.5 5.6a3.3 3.3 0 0 1 0 6.2M17.5 14.9c1.9.6 3 2.5 3 5.1" />
    </svg>
  ),
  user: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M5 20c0-3.4 3-5.6 7-5.6s7 2.2 7 5.6" />
    </svg>
  ),
  check: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m5 12.5 4.5 4.5L19 7" />
    </svg>
  ),
  checkSquare: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M20 11v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
      <path d="m8.5 11.5 3 3L21 5" />
    </svg>
  ),
  clipboard: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="5" y="5" width="14" height="16" rx="2" />
      <path d="M9 5V3.8A.8.8 0 0 1 9.8 3h4.4a.8.8 0 0 1 .8.8V5" />
      <path d="M9 11h6M9 15h4" />
    </svg>
  ),
  alert: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M10.3 4.3 2.9 17a1.9 1.9 0 0 0 1.7 2.9h14.8a1.9 1.9 0 0 0 1.7-2.9L13.7 4.3a1.9 1.9 0 0 0-3.4 0Z" />
      <path d="M12 9.5v4M12 17h.01" />
    </svg>
  ),
  shield: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 3.2 5 5.8v5.6c0 4.3 2.9 7.7 7 9.4 4.1-1.7 7-5.1 7-9.4V5.8z" />
      <path d="m9.2 12.2 2 2 3.6-3.8" />
    </svg>
  ),
  money: (p: IconProps) => (
    <svg {...base(p)}>
      <ellipse cx="12" cy="6.6" rx="7.6" ry="3.1" />
      <path d="M4.4 6.6v10.8c0 1.7 3.4 3.1 7.6 3.1s7.6-1.4 7.6-3.1V6.6" />
      <path d="M4.4 12c0 1.7 3.4 3.1 7.6 3.1s7.6-1.4 7.6-3.1" />
    </svg>
  ),
  chart: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4 20V4M4 20h16" />
      <path d="M8 17v-5M12 17V8M16 17v-7" />
    </svg>
  ),
  trend: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m3.5 15.5 5-5 3.5 3.5 6-6.5" />
      <path d="M14.5 7.5h4v4" />
    </svg>
  ),
  doc: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M14 3.5H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5z" />
      <path d="M14 3.5v5h5M9 13h6M9 16.5h4" />
    </svg>
  ),
  mic: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="9.2" y="3" width="5.6" height="11" rx="2.8" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
    </svg>
  ),
  book: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4 5.2A1.7 1.7 0 0 1 5.7 3.5H19v14.8H5.7A1.7 1.7 0 0 0 4 20z" />
      <path d="M4 18.3A1.7 1.7 0 0 1 5.7 16.6H19" />
    </svg>
  ),
  handshake: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m3.5 12 3-3 3 2.5 2.5-2 3 2.5 2.5-2.5 3 3" />
      <path d="M9.5 11.5 12 14l2 -1.6M12 14l2 2M14 16l1.8 1.8" />
    </svg>
  ),
  plane: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M21 15.5 13.8 12V5.8a1.8 1.8 0 1 0-3.6 0V12L3 15.5v2l7.2-2.2v3.6L7.8 20.5V22l4.2-1.2 4.2 1.2v-1.5l-2.4-1.6v-3.6L21 17.5z" />
    </svg>
  ),
  palette: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 2-.9 2-2 0-.5-.2-1-.6-1.3-.3-.3-.5-.8-.5-1.2 0-1 .8-1.8 1.8-1.8h1.6a4.2 4.2 0 0 0 4.2-4.2c0-3.6-3.8-6.5-8.5-6.5Z" />
      <circle cx="7.8" cy="11" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="10.2" cy="7.4" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14.6" cy="7.4" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  ),
  cog: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.6 14.6a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-1.7-.3 1.5 1.5 0 0 0-.9 1.4v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-1-1.4 1.5 1.5 0 0 0-1.7.3l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0 .3-1.7 1.5 1.5 0 0 0-1.4-.9h-.2a1.8 1.8 0 0 1 0-3.6h.1a1.5 1.5 0 0 0 1.4-1 1.5 1.5 0 0 0-.3-1.7l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3h.1a1.5 1.5 0 0 0 .9-1.4v-.2a1.8 1.8 0 0 1 3.6 0v.1a1.5 1.5 0 0 0 .9 1.4 1.5 1.5 0 0 0 1.7-.3l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7v.1a1.5 1.5 0 0 0 1.4.9h.2a1.8 1.8 0 0 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9Z" />
    </svg>
  ),
  plug: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M9 3v5M15 3v5" />
      <path d="M6.5 8h11v3.5a5.5 5.5 0 0 1-11 0z" />
      <path d="M12 17v4" />
    </svg>
  ),
  help: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.8-.9 1.4v.4M12 16.8h.01" />
    </svg>
  ),
  plus: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  x: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
  chevronDown: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m6 9.5 6 6 6-6" />
    </svg>
  ),
  chevronRight: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m9.5 6 6 6-6 6" />
    </svg>
  ),
  chevronLeft: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m14.5 6-6 6 6 6" />
    </svg>
  ),
  arrowUp: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  ),
  arrowDown: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </svg>
  ),
  arrowRight: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  external: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M14 4h6v6M20 4l-8.5 8.5" />
      <path d="M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />
    </svg>
  ),
  more: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="5.5" cy="12" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.3" fill="currentColor" />
    </svg>
  ),
  filter: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M3.5 5.5h17l-6.5 7.6V19l-4 2v-7.9z" />
    </svg>
  ),
  download: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 4v10M8 10.5l4 4 4-4" />
      <path d="M4.5 18.5v.5a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-.5" />
    </svg>
  ),
  upload: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 16V5.5M8 9.5l4-4 4 4" />
      <path d="M4.5 18.5v.5a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-.5" />
    </svg>
  ),
  mail: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.6 7 8.4 6 8.4-6" />
    </svg>
  ),
  lock: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="4.5" y="10" width="15" height="10.5" rx="2" />
      <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
    </svg>
  ),
  eye: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  eyeOff: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.8c6 0 9.5 6.2 9.5 6.2a17 17 0 0 1-3.2 4" />
      <path d="M6.4 7.9A17 17 0 0 0 2.5 12s3.5 6.2 9.5 6.2a9.4 9.4 0 0 0 4-.9" />
      <path d="m3.5 3.5 17 17M10 10a3 3 0 0 0 4 4" />
    </svg>
  ),
  pin: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  ),
  building: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4.5 20.5V5a1.5 1.5 0 0 1 1.5-1.5h7A1.5 1.5 0 0 1 14.5 5v15.5" />
      <path d="M14.5 10H18a1.5 1.5 0 0 1 1.5 1.5v9M3 20.5h18" />
      <path d="M7.5 7.5h3.5M7.5 11h3.5M7.5 14.5h3.5" />
    </svg>
  ),
  globe: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.2 2.4 3.3 5.4 3.3 8.5s-1.1 6.1-3.3 8.5c-2.2-2.4-3.3-5.4-3.3-8.5S9.8 5.9 12 3.5Z" />
    </svg>
  ),
  layers: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m12 3 8.5 4.5L12 12 3.5 7.5z" />
      <path d="m3.5 12 8.5 4.5 8.5-4.5M3.5 16.5 12 21l8.5-4.5" />
    </svg>
  ),
  target: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.8" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  clock: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.2 1.9" />
    </svg>
  ),
  flag: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M5.5 21V4M5.5 5h11l-2 3.5 2 3.5h-11" />
    </svg>
  ),
  link: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 1 0-5.7-5.7L11.8 6.5" />
      <path d="M13.5 10.5a4 4 0 0 0-5.7 0L5 13.3a4 4 0 1 0 5.7 5.7l1.4-1.5" />
    </svg>
  ),
  refresh: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M20 11.5A8 8 0 0 0 6.3 6.3L4 8.5" />
      <path d="M4 4v4.5h4.5M4 12.5A8 8 0 0 0 17.7 17.7L20 15.5" />
      <path d="M20 20v-4.5h-4.5" />
    </svg>
  ),
  send: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m21 3-9.5 9.5M21 3l-6.5 18-3.9-8.1L2.5 9z" />
    </svg>
  ),
  megaphone: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4 10v4a1.5 1.5 0 0 0 1.5 1.5H8l8 4.5V5.5L8 10z" />
      <path d="M19 9.5a3.5 3.5 0 0 1 0 5M8 15.5V20" />
    </svg>
  ),
  monitor: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M9 20.5h6M12 16.5v4" />
    </svg>
  ),
  box: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="m12 3 8 4.2v9.6L12 21l-8-4.2V7.2z" />
      <path d="m4 7.2 8 4.3 8-4.3M12 21v-9.5" />
    </svg>
  ),
  scan: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4 8.5V6a2 2 0 0 1 2-2h2.5M15.5 4H18a2 2 0 0 1 2 2v2.5M20 15.5V18a2 2 0 0 1-2 2h-2.5M8.5 20H6a2 2 0 0 1-2-2v-2.5" />
      <path d="M4 12h16" />
    </svg>
  ),
  branch: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="7" cy="5.5" r="2.3" />
      <circle cx="7" cy="18.5" r="2.3" />
      <circle cx="17" cy="9.5" r="2.3" />
      <path d="M7 7.8v8.4M17 11.8c0 3-2.5 4.2-5.5 4.6" />
    </svg>
  ),
  history: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.5" />
      <path d="M3.5 4v4.5H8M12 7.5V12l3 1.8" />
    </svg>
  ),
  bolt: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M13.5 3 5 13.5h5.5L10 21l8.5-10.5H13z" />
    </svg>
  ),
  brain: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M9.5 4.5a2.8 2.8 0 0 0-2.8 2.8 2.6 2.6 0 0 0-1.9 4.3 2.6 2.6 0 0 0 1.2 4.3A2.7 2.7 0 0 0 9.5 20a2.5 2.5 0 0 0 2.5-2.5v-11a2.5 2.5 0 0 0-2.5-2Z" />
      <path d="M14.5 4.5a2.8 2.8 0 0 1 2.8 2.8 2.6 2.6 0 0 1 1.9 4.3 2.6 2.6 0 0 1-1.2 4.3A2.7 2.7 0 0 1 14.5 20 2.5 2.5 0 0 1 12 17.5v-11a2.5 2.5 0 0 1 2.5-2Z" />
    </svg>
  ),
  list: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M8.5 6.5h12M8.5 12h12M8.5 17.5h12M4 6.5h.01M4 12h.01M4 17.5h.01" />
    </svg>
  ),
  file: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M13.5 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.5z" />
      <path d="M13.5 3.5v5h5" />
    </svg>
  ),
  bed: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M3 18.5v-11M3 12h18v6.5M3 18.5h18" />
      <path d="M7.5 12V9.5h5.5a3 3 0 0 1 3 2.5" />
      <circle cx="7.5" cy="9.5" r="0" />
    </svg>
  ),
  car: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M5 16.5h14M4 16.5v-3.2L6 8h12l2 5.3v3.2" />
      <circle cx="7.5" cy="17.5" r="1.6" />
      <circle cx="16.5" cy="17.5" r="1.6" />
    </svg>
  ),
  ticket: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M4 8.5V6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v2a2.4 2.4 0 0 0 0 7v2a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-2a2.4 2.4 0 0 0 0-7Z" />
      <path d="M14 5v14" strokeDasharray="2 3" />
    </svg>
  ),
  award: (p: IconProps) => (
    <svg {...base(p)}>
      <circle cx="12" cy="9" r="5.5" />
      <path d="m8.5 13.5-1 7 4.5-2.4 4.5 2.4-1-7" />
    </svg>
  ),
  table: (p: IconProps) => (
    <svg {...base(p)}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M9 9.5v10M3.5 14.5h17" />
    </svg>
  ),
  wallet: (p: IconProps) => (
    <svg {...base(p)}>
      <path d="M3.5 7.5A2 2 0 0 1 5.5 5.5h11a2 2 0 0 1 2 2v1" />
      <rect x="3.5" y="7.5" width="17" height="11.5" rx="2" />
      <circle cx="16" cy="13.2" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  ),
};

export type IconName = keyof typeof Icon;
